import { fetchJsonApi } from "../apiClient";

// Read-through API caches must not serve a value for the entire tab session — a mid-session
// update would otherwise never appear. Entries older than the TTL are re-fetched. In-flight
// de-duplication still prevents concurrent duplicate calls within the window.
export const DEFAULT_CACHE_TTL_MS = 5 * 60 * 1000;

function isFresh(at, ttlMs) {
  return at > 0 && Date.now() - at < ttlMs;
}

/** Cache + in-flight-request dedup for a `GET <path>` index endpoint returning `{ items: [...] }`. */
export function createIndexCache(path, { ttlMs = DEFAULT_CACHE_TTL_MS } = {}) {
  let cached = null;
  let cachedAt = 0;
  let inFlightRequest = null;

  return async function getIndex() {
    if (Array.isArray(cached) && isFresh(cachedAt, ttlMs)) return cached;
    if (inFlightRequest) return inFlightRequest;

    inFlightRequest = fetchJsonApi(path)
      .then((data) => {
        cached = Array.isArray(data?.items) ? data.items : [];
        cachedAt = Date.now();
        return cached;
      })
      .finally(() => {
        inFlightRequest = null;
      });

    return inFlightRequest;
  };
}

/**
 * Cache + in-flight-request dedup for a `GET <path>/:key` endpoint, one entry per key.
 * `normalizeKey` controls cache-key casing/shape; `transform` shapes the raw response into
 * whatever the caller stores/returns (a bare items array, or an object wrapping it).
 */
export function createKeyedResourceCache({
  fetchPath,
  normalizeKey = (k) => k,
  transform = (data) => data,
  ttlMs = DEFAULT_CACHE_TTL_MS,
}) {
  const cache = new Map(); // key -> { value, at }
  const inFlight = new Map();

  return async function getItem(rawKey) {
    const key = normalizeKey(rawKey);
    const entry = cache.get(key);
    if (entry && isFresh(entry.at, ttlMs)) return entry.value;
    if (inFlight.has(key)) return inFlight.get(key);

    const request = fetchJsonApi(fetchPath(key))
      .then((data) => {
        const result = transform(data, key);
        cache.set(key, { value: result, at: Date.now() });
        return result;
      })
      .finally(() => {
        inFlight.delete(key);
      });

    inFlight.set(key, request);
    return request;
  };
}
